import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight, ChevronLeft, Search, Check, X, SlidersHorizontal } from 'lucide-react';
import { useScrollLock } from '@/lib/scrollLock';

const DARK  = '#2A1F14';
const GOLD  = '#e05590';
const LINE  = 'rgba(210,60,120,0.2)';
const MUTED = 'rgba(42,31,20,0.42)';
const CREAM = '#FAF7F2';

/* ─── Definitions ─────────────────────────────────────────── */
/** All possible occasion filter options.
 *  `handle` is the Shopify collection handle used to match products.
 *  Only options whose handle appears in the current collection's products
 *  are shown — computed dynamically by each page component.
 */
export const OKAZJE: { value: string; label: string; emoji: string; handle: string }[] = [
  { value: 'Urodziny',              label: 'Urodziny',              emoji: '🎂', handle: 'urodziny' },
  { value: 'Imieniny',              label: 'Imieniny',              emoji: '🎉', handle: 'imieniny' },
  { value: 'Rocznica',              label: 'Rocznica',              emoji: '💞', handle: 'rocznica' },
  { value: 'Przeprosiny',           label: 'Przeprosiny',           emoji: '🕊️', handle: 'przeprosiny' },
  { value: 'Gratulacje',            label: 'Gratulacje',            emoji: '🥂', handle: 'gratulacje' },
  { value: 'Podziękowania',         label: 'Podziękowania',         emoji: '🙏', handle: 'podziekowania' },
  { value: 'Niespodzianka',         label: 'Niespodzianka',         emoji: '🎁', handle: 'niespodzianka' },
  { value: 'Komunia',               label: 'Komunia',               emoji: '✝️', handle: 'komunia' },
  { value: 'Obrona',                label: 'Obrona',                emoji: '🎓', handle: 'obrona' },
  { value: 'Zaręczyny',             label: 'Zaręczyny',             emoji: '💍', handle: 'zareczyny' },
  { value: 'Dla Pary Młodej',       label: 'Dla Pary Młodej',       emoji: '💒', handle: 'kwiaty-dla-pary-mlodej' },
  { value: 'Dla mamy',              label: 'Dla mamy',              emoji: '👩', handle: 'dla-mamy' },
  { value: 'Dla żony',              label: 'Dla żony',              emoji: '💝', handle: 'dla-zony' },
  { value: 'Dla dziewczyny',        label: 'Dla dziewczyny',        emoji: '💕', handle: 'dla-dziewczyny' },
  { value: 'Dla babci',             label: 'Dla babci',             emoji: '👵', handle: 'dla-babci' },
  { value: 'Dla mężczyzny',         label: 'Dla mężczyzny',         emoji: '🎩', handle: 'dla-mezczyzny' },
  { value: 'Dla przyjaciela',       label: 'Dla przyjaciela',       emoji: '🤝', handle: 'dla-przyjaciela' },
  { value: 'Dzień Babci i Dziadka', label: 'Dzień Babci i Dziadka', emoji: '👴', handle: 'dzien-babci-i-dziadka' },
  { value: 'Walentynki',            label: 'Walentynki',            emoji: '💌', handle: 'walentynki' },
  { value: 'Dzień Kobiet',          label: 'Dzień Kobiet',          emoji: '👑', handle: 'dzien-kobiet' },
  { value: 'Wielkanoc',             label: 'Wielkanoc',             emoji: '🐣', handle: 'wielkanoc' },
  { value: 'Dzień Matki',           label: 'Dzień Matki',           emoji: '🌸', handle: 'dzien-matki' },
  { value: 'Dzień Dziecka',         label: 'Dzień Dziecka',         emoji: '🧒', handle: 'dzien-dziecka' },
  { value: 'Dzień Ojca',            label: 'Dzień Ojca',            emoji: '👨', handle: 'dzien-ojca' },
  { value: 'Wszystkich Świętych',   label: 'Wszystkich Świętych',   emoji: '🕯️', handle: 'wszystkich-swietych' },
  { value: 'Boże Narodzenie',       label: 'Boże Narodzenie',       emoji: '🎄', handle: 'boze-narodzenie' },
];

export const KWIATY: { value: string; label: string; emoji: string; handle: string }[] = [
  { value: 'Róże',          label: 'Róże',          emoji: '🌹', handle: 'roze' },
  { value: 'Lilie',         label: 'Lilie',         emoji: '💐', handle: 'lilie' },
  { value: 'Gerbery',       label: 'Gerbery',       emoji: '🌼', handle: 'gerbery' },
  { value: 'Goździki',      label: 'Goździki',      emoji: '🌺', handle: 'gozdziki' },
  { value: 'Słoneczniki',   label: 'Słoneczniki',   emoji: '🌻', handle: 'sloneczniki' },
  { value: 'Alstroemerie',  label: 'Alstroemerie',  emoji: '🌸', handle: 'alstroemerie' },
  { value: 'Eustomy',       label: 'Eustomy',       emoji: '🌷', handle: 'eustomy' },
  { value: 'Gipsówka',      label: 'Gipsówka',      emoji: '🤍', handle: 'gipsowka' },
  { value: 'Margaretki',    label: 'Margaretki',    emoji: '🌼', handle: 'margaretki' },
  { value: 'Piwonie',       label: 'Piwonie',       emoji: '🌸', handle: 'piwonie' },
  { value: 'Hortensje',     label: 'Hortensje',     emoji: '💮', handle: 'hortensje' },
  { value: 'Lewkonie',      label: 'Lewkonie',      emoji: '🌸', handle: 'lewkonie' },
  { value: 'Frezje',        label: 'Frezje',        emoji: '🌸', handle: 'frezje' },
  { value: 'Jaskier',       label: 'Jaskier',       emoji: '🌼', handle: 'jaskier' },
  { value: 'Anturium',      label: 'Anturium',      emoji: '🌺', handle: 'anturium' },
  { value: 'Lwie paszcze',  label: 'Lwie paszcze',  emoji: '🌷', handle: 'lwie-paszcze' },
  { value: 'Dalie',         label: 'Dalie',         emoji: '🌸', handle: 'dalie' },
  { value: 'Tulipany',      label: 'Tulipany',      emoji: '🌷', handle: 'tulipany' },
];

export const KOLORY: { value: string; label: string; hex: string }[] = [
  { value: 'czerwony',     label: 'Czerwone',     hex: '#c0392b' },
  { value: 'rozowy',       label: 'Różowe',       hex: '#e8a0b4' },
  { value: 'bialy',        label: 'Białe',        hex: '#f0ede8' },
  { value: 'zolty',        label: 'Żółte',        hex: '#f4d03f' },
  { value: 'fioletowy',    label: 'Fioletowe',    hex: '#8e44ad' },
  { value: 'pomaranczowy', label: 'Pomarańczowe', hex: '#e67e22' },
  { value: 'kremowy',      label: 'Kremowe',      hex: '#f5e6c8' },
  { value: 'burgundowy',   label: 'Burgundowe',   hex: '#800020' },
];


export const KOMPOZYCJE: { value: string; label: string; emoji: string }[] = [
  { value: 'Jednolite',         label: 'Jednolite',          emoji: '🌹' },
  { value: 'Mieszane',          label: 'Mieszane',           emoji: '💐' },
  { value: 'Sezonowe',          label: 'Sezonowe',           emoji: '🍂' },
  { value: 'Premium',           label: 'Premium',            emoji: '✨' },
  { value: 'Klasyczne',         label: 'Klasyczne',          emoji: '🎀' },
  { value: 'Nowoczesne',        label: 'Nowoczesne',         emoji: '🔷' },
  { value: 'Naturalne',         label: 'Naturalne / rustykalne', emoji: '🌿' },
];

export const STYLE: { value: string; label: string; emoji: string }[] = [
  { value: 'Romantyczny',    label: 'Romantyczny',    emoji: '💕' },
  { value: 'Elegancki',      label: 'Elegancki',      emoji: '🥂' },
  { value: 'Delikatny',      label: 'Delikatny',      emoji: '🌸' },
  { value: 'Luksusowy',      label: 'Luksusowy',      emoji: '💎' },
  { value: 'Minimalistyczny',label: 'Minimalistyczny',emoji: '🤍' },
  { value: 'Naturalny',      label: 'Naturalny',      emoji: '🍃' },
  { value: 'Kolorowy',       label: 'Kolorowy',       emoji: '🌈' },
];

export interface FilterState {
  okazje:     string[];
  kwiaty:     string[];
  kolory:     string[];
  kompozycje: string[];
  style:      string[];
  priceRange: [number, number];
}

export const EMPTY_FILTERS: FilterState = {
  okazje: [], kwiaty: [], kolory: [],
  kompozycje: [], style: [], priceRange: [0, 1000],
};

export function hasActiveFilters(f: FilterState, maxPrice: number): boolean {
  return (
    f.okazje.length > 0 || f.kwiaty.length > 0 ||
    f.kolory.length > 0 ||
    f.kompozycje.length > 0 || f.style.length > 0 ||
    f.priceRange[0] > 0 || f.priceRange[1] < maxPrice
  );
}

export type Section = 'okazje' | 'kwiaty' | 'kolory' | 'cena' | 'kompozycje' | 'style' | null;

/* ─── Tab button ──────────────────────────────────────────── */
function Tab({
  label, active, open, onClick,
}: { label: string; active: boolean; open: boolean; onClick: () => void }) {
  const bg   = open ? DARK : active ? GOLD : 'transparent';
  const col  = open || active ? '#fff' : 'rgba(42,31,20,0.6)';
  const bord = open ? DARK : active ? GOLD : 'rgba(42,31,20,0.18)';

  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        padding: '10px 20px',
        minHeight: 44,
        border: `1px solid ${bord}`,
        background: bg,
        cursor: 'pointer',
        fontFamily: 'DM Sans, sans-serif',
        fontSize: 10, letterSpacing: '0.13em',
        textTransform: 'uppercase',
        color: col,
        fontWeight: 600,
        transition: 'all 0.18s',
        whiteSpace: 'nowrap',
        boxShadow: open || active ? '0 2px 12px rgba(42,31,20,0.12)' : 'none',
      }}
    >
      {label}
      <ChevronDown
        size={11}
        color={open || active ? 'rgba(255,255,255,0.75)' : 'rgba(42,31,20,0.35)'}
        style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
      />
    </button>
  );
}

/* ─── Icon chip ───────────────────────────────────────────── */
function IconChip({
  emoji, label, selected, onClick,
}: { emoji: string; label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '9px 18px',
        minHeight: 44,
        border: `1px solid ${selected ? GOLD : 'rgba(42,31,20,0.12)'}`,
        background: selected ? GOLD : '#fff',
        cursor: 'pointer',
        fontFamily: 'DM Sans, sans-serif',
        fontSize: 12, letterSpacing: '0.03em',
        color: selected ? '#fff' : DARK,
        fontWeight: selected ? 600 : 400,
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
        boxShadow: selected ? '0 2px 10px rgba(210,60,120,0.3)' : 'none',
      }}
    >
      <span style={{ fontSize: 17, lineHeight: 1 }}>{emoji}</span>
      {label}
    </button>
  );
}

/* ─── Price inputs ─────────────────────────────────────────── */
function PriceInputs({
  lo, hi, maxPrice, onChange,
}: { lo: number; hi: number; maxPrice: number; onChange: (v: [number, number]) => void }) {
  const inputStyle: React.CSSProperties = {
    width: 100, padding: '10px 14px', minHeight: 44,
    border: `1px solid rgba(42,31,20,0.18)`,
    background: '#fff',
    fontFamily: 'DM Sans, sans-serif', fontSize: 16, color: DARK,
    outline: 'none', textAlign: 'center',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED }}>Od</span>
        <div style={{ position: 'relative' }}>
          <input
            type="number" min={0} max={hi - 1} value={lo}
            onChange={e => onChange([Math.max(0, Math.min(+e.target.value, hi - 1)), hi])}
            style={inputStyle}
          />
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: MUTED, pointerEvents: 'none' }}>zł</span>
        </div>
      </div>

      <div style={{ width: 20, height: 1, background: 'rgba(42,31,20,0.2)', marginTop: 18 }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED }}>Do</span>
        <div style={{ position: 'relative' }}>
          <input
            type="number" min={lo + 1} max={maxPrice} value={hi}
            onChange={e => onChange([lo, Math.max(lo + 1, Math.min(+e.target.value, maxPrice))])}
            style={inputStyle}
          />
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: MUTED, pointerEvents: 'none' }}>zł</span>
        </div>
      </div>

      {(lo > 0 || hi < maxPrice) && (
        <button
          onClick={() => onChange([0, maxPrice])}
          style={{ marginTop: 18, padding: '10px 14px', minHeight: 44, border: `1px solid rgba(42,31,20,0.18)`, background: 'none', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: MUTED }}
        >
          Reset
        </button>
      )}
    </div>
  );
}

/* ─── Expanded panel content ──────────────────────────────── */
function PanelContent({
  section, filters, onChange, maxPrice, kompozycjeOptions, okazjeOptions, kwiatyOptions,
}: {
  section: Section;
  filters: FilterState;
  onChange: (f: FilterState) => void;
  maxPrice: number;
  kompozycjeOptions?: { value: string; label: string; emoji?: string }[];
  okazjeOptions?: { value: string; label: string; emoji: string }[];
  kwiatyOptions?: { value: string; label: string; emoji: string }[];
}) {
  const toggle = <K extends 'okazje' | 'kwiaty' | 'kompozycje' | 'style'>(key: K, value: string) => {
    const cur = filters[key] as string[];
    onChange({ ...filters, [key]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] });
  };

  const [lo, hi] = filters.priceRange;

  if (section === 'okazje') {
    const opts = okazjeOptions ?? OKAZJE;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {opts.map(o => (
          <IconChip key={o.value} emoji={o.emoji} label={o.label}
            selected={filters.okazje.includes(o.value)}
            onClick={() => toggle('okazje', o.value)}
          />
        ))}
      </div>
    );
  }

  if (section === 'kwiaty') {
    const opts = kwiatyOptions ?? KWIATY;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {opts.map(k => (
          <IconChip key={k.value} emoji={k.emoji} label={k.label}
            selected={filters.kwiaty.includes(k.value)}
            onClick={() => toggle('kwiaty', k.value)}
          />
        ))}
      </div>
    );
  }

  if (section === 'kolory') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        {KOLORY.map(c => (
          <button
            key={c.value}
            onClick={() => onChange({
              ...filters,
              kolory: filters.kolory.includes(c.value)
                ? filters.kolory.filter(v => v !== c.value)
                : [...filters.kolory, c.value],
            })}
            title={c.label}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '6px', minWidth: 44, minHeight: 44, justifyContent: 'center',
            }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: '50%', background: c.hex,
              border: filters.kolory.includes(c.value) ? `3px solid ${GOLD}` : `2px solid rgba(42,31,20,0.14)`,
              boxShadow: filters.kolory.includes(c.value) ? `0 0 0 2px #fff, 0 0 0 4px ${GOLD}` : 'none',
              transition: 'all 0.15s',
            }} />
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, color: MUTED }}>{c.label}</span>
          </button>
        ))}
      </div>
    );
  }

  if (section === 'cena') {
    return <PriceInputs lo={lo} hi={hi} maxPrice={maxPrice} onChange={v => onChange({ ...filters, priceRange: v })} />;
  }

  if (section === 'kompozycje') {
    const opts = kompozycjeOptions ?? KOMPOZYCJE;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {opts.map(k => (
          <IconChip key={k.value} emoji={k.emoji ?? ''} label={k.label}
            selected={filters.kompozycje.includes(k.value)}
            onClick={() => toggle('kompozycje', k.value)}
          />
        ))}
      </div>
    );
  }

  if (section === 'style') {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {STYLE.map(s => (
          <IconChip key={s.value} emoji={s.emoji} label={s.label}
            selected={filters.style.includes(s.value)}
            onClick={() => toggle('style', s.value)}
          />
        ))}
      </div>
    );
  }

  return null;
}

/* ─── Grid tile — short option lists (kompozycje, styl) ──── */
function OptionTile({
  emoji, label, checked, onClick,
}: { emoji: string; label: string; checked: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        padding: '18px 8px', minHeight: 84,
        borderRadius: 10,
        border: `1.5px solid ${checked ? GOLD : LINE}`,
        background: checked ? 'rgba(210,60,120,0.06)' : CREAM,
        cursor: 'pointer',
      }}
    >
      <span style={{ fontSize: 24, lineHeight: 1 }}>{emoji}</span>
      <span style={{
        fontFamily: 'DM Sans, sans-serif', fontSize: 11.5, textAlign: 'center', lineHeight: 1.25,
        color: DARK, fontWeight: checked ? 600 : 400,
      }}>
        {label}
      </span>
    </button>
  );
}

/* ─── List row — long, searchable option lists (okazje, kwiaty) ── */
function OptionRow({
  emoji, label, checked, onClick,
}: { emoji: string; label: string; checked: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        width: '100%', minHeight: 48, padding: '0 20px',
        background: checked ? 'rgba(210,60,120,0.06)' : '#fff',
        border: 'none', borderBottom: `1px solid ${LINE}`,
        cursor: 'pointer', textAlign: 'left',
      }}
    >
      <span style={{ fontSize: 17, width: 22, textAlign: 'center', flexShrink: 0 }}>{emoji}</span>
      <span style={{ flex: 1, fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: DARK, fontWeight: checked ? 600 : 400 }}>
        {label}
      </span>
      <span style={{
        width: 20, height: 20, borderRadius: 5, flexShrink: 0,
        border: `1.5px solid ${checked ? GOLD : 'rgba(42,31,20,0.25)'}`,
        background: checked ? GOLD : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.12s, border-color 0.12s',
      }}>
        {checked && <Check size={13} color="#fff" strokeWidth={3} />}
      </span>
    </button>
  );
}

/* ─── Content of one drill-down screen ───────────────────── */
function DrillContent({
  section, query, filters, onChange, maxPrice, kompozycjeOptions, okazjeOptions, kwiatyOptions,
}: {
  section: NonNullable<Section>;
  query: string;
  filters: FilterState; onChange: (f: FilterState) => void; maxPrice: number;
  kompozycjeOptions?: { value: string; label: string; emoji?: string }[];
  okazjeOptions?: { value: string; label: string; emoji: string }[];
  kwiatyOptions?: { value: string; label: string; emoji: string }[];
}) {
  const toggle = <K extends 'okazje' | 'kwiaty' | 'kompozycje' | 'style'>(key: K, value: string) => {
    const cur = filters[key] as string[];
    onChange({ ...filters, [key]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] });
  };

  // Długie, przeszukiwalne listy — Okazja (26 opcji) i Rodzaj kwiatów (19).
  if (section === 'okazje' || section === 'kwiaty') {
    const opts = section === 'okazje' ? (okazjeOptions ?? OKAZJE) : (kwiatyOptions ?? KWIATY);
    const q = query.trim().toLowerCase();
    const filtered = q ? opts.filter(o => o.label.toLowerCase().includes(q)) : opts;
    const selectedValues = section === 'okazje' ? filters.okazje : filters.kwiaty;

    if (filtered.length === 0) {
      return (
        <p style={{ padding: '36px 20px', textAlign: 'center', fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: MUTED }}>
          Brak wyników dla „{query.trim()}”
        </p>
      );
    }
    return (
      <div>
        {filtered.map(o => (
          <OptionRow
            key={o.value}
            emoji={o.emoji}
            label={o.label}
            checked={selectedValues.includes(o.value)}
            onClick={() => toggle(section, o.value)}
          />
        ))}
      </div>
    );
  }

  // Krótkie listy — Rodzaj kompozycji, Styl bukietu — jako siatka kafelków.
  if (section === 'kompozycje' || section === 'style') {
    const opts = section === 'kompozycje' ? (kompozycjeOptions ?? KOMPOZYCJE) : STYLE;
    const selectedValues = section === 'kompozycje' ? filters.kompozycje : filters.style;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: '16px 20px 20px' }}>
        {opts.map(o => (
          <OptionTile
            key={o.value}
            emoji={o.emoji ?? ''}
            label={o.label}
            checked={selectedValues.includes(o.value)}
            onClick={() => toggle(section, o.value)}
          />
        ))}
      </div>
    );
  }

  // Kolor i Cena — istniejące renderowanie (swatch/koła i pola cenowe), bez zmian.
  return (
    <div style={{ padding: '16px 20px 20px' }}>
      <PanelContent section={section} filters={filters} onChange={onChange} maxPrice={maxPrice} />
    </div>
  );
}

/**
 * Ile opcji ma sens od razu odsłonić bez szukania — powyżej tego progu
 * ekran dostaje pole wyszukiwania. Okazja (26) i Rodzaj kwiatów (19) go
 * przekraczają; Kompozycja/Styl (≤7) — nie.
 */
function isSearchable(count: number): boolean {
  return count > 8;
}

/* ─── Selected filter chip bar (mobile, inside sheet) ──────── */
function SelectedFilterBar({
  label, values, onRemove, onClearAll,
}: {
  label: string;
  values: string[];
  onRemove: (v: string) => void;
  onClearAll: () => void;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 20px',
    }}>
      {/* Clear-all – black circle X, stays fixed */}
      <button
        onClick={onClearAll}
        aria-label={`Usuń wszystkie filtry: ${label}`}
        style={{
          flexShrink: 0,
          width: 24, height: 24,
          borderRadius: '50%',
          background: DARK,
          border: 'none',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 0,
        }}
      >
        <X size={12} color="#fff" strokeWidth={2.5} />
      </button>

      {/* Section label – stays fixed */}
      <span style={{
        flexShrink: 0,
        fontFamily: 'DM Sans, sans-serif',
        fontSize: 11,
        color: MUTED,
        whiteSpace: 'nowrap',
      }}>
        {label}
      </span>

      {/* Scrollable chips – overflow-x only, no wrap */}
      <div
        className="no-scrollbar"
        style={{
          display: 'flex',
          flexWrap: 'nowrap',
          overflowX: 'auto',
          gap: 6,
          // @ts-ignore — non-standard but valid on iOS Safari
          WebkitOverflowScrolling: 'touch',
          /* peek at next chip to hint scrollability */
          paddingRight: 24,
        }}
      >
        {values.map(v => (
          <button
            key={v}
            onClick={() => onRemove(v)}
            style={{
              flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 8px 4px 11px',
              borderRadius: 999,
              background: 'rgba(210,60,120,0.07)',
              border: '1px solid rgba(210,60,120,0.25)',
              cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 12,
              color: DARK,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              lineHeight: 1,
            }}
          >
            {v}
            <span style={{
              color: GOLD, fontSize: 14, fontWeight: 600, lineHeight: 1, marginLeft: 1,
            }}>×</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Mobile filter bottom sheet ─────────────────────────── */
function MobileFilterSheet({
  open, onClose, filters, onChange, maxPrice, visibleSections, kompozycjeOptions, kompozycjeLabel,
  okazjeOptions, kwiatyOptions,
}: {
  open: boolean; onClose: () => void;
  filters: FilterState; onChange: (f: FilterState) => void; maxPrice: number;
  visibleSections?: Section[];
  kompozycjeOptions?: { value: string; label: string; emoji?: string }[];
  kompozycjeLabel?: string;
  okazjeOptions?: { value: string; label: string; emoji: string }[];
  kwiatyOptions?: { value: string; label: string; emoji: string }[];
}) {
  // Zamiast rozwijania sekcji w miejscu (nieczytelne przy 26 opcjach —
  // Okazja), dotknięcie kategorii otwiera jej WŁASNY ekran, który wjeżdża
  // znad menu. drillKey = która kategoria jest aktualnie otwarta (null =
  // widoczne samo menu). query = tekst wyszukiwania na ekranie danej
  // kategorii, resetowany przy każdym nowym wejściu.
  const [drillKey, setDrillKey] = useState<Section>(null);
  const [query, setQuery] = useState('');

  // Keep the lock through AnimatePresence's exit animation.
  const releaseScrollLock = useScrollLock(open, false);

  // Arkusz zawsze rozciąga się od dołu headera do dołu ekranu — niezależnie
  // od ilości sekcji i rozmiaru viewportu. Header ustawia --header-height
  // przez ResizeObserver (Header.tsx). top: var(--header-height) jest STAŁE
  // i nie zmienia się przy show/hide paska adresu Safari (top jest zakotwiczone
  // do góry viewportu, nie do dołu), więc nagłówek drill-downa nie ucieka.

  const ALL_SECTIONS: { key: Section; label: string }[] = [
    { key: 'kompozycje', label: kompozycjeLabel ?? 'Kompozycja' },
    { key: 'style',      label: 'Styl bukietu' },
    { key: 'okazje',     label: 'Okazja' },
    { key: 'kwiaty',     label: 'Rodzaj kwiatów' },
    { key: 'kolory',     label: 'Kolor' },
    { key: 'cena',       label: 'Cena' },
  ];
  const SECTIONS = visibleSections
    ? ALL_SECTIONS.filter(s => visibleSections.includes(s.key))
    : ALL_SECTIONS;

  function sectionCount(key: Section): number {
    if (!key) return 0;
    if (key === 'okazje') return filters.okazje.length;
    if (key === 'kwiaty') return filters.kwiaty.length;
    if (key === 'kolory') return filters.kolory.length;
    if (key === 'kompozycje') return filters.kompozycje.length;
    if (key === 'style') return filters.style.length;
    if (key === 'cena') return (filters.priceRange[0] > 0 || filters.priceRange[1] < maxPrice) ? 1 : 0;
    return 0;
  }

  function optionCount(key: Section): number {
    if (key === 'okazje') return (okazjeOptions ?? OKAZJE).length;
    if (key === 'kwiaty') return (kwiatyOptions ?? KWIATY).length;
    return 0;
  }

  function openDrill(key: Section) {
    setQuery('');
    setDrillKey(key);
  }
  function closeDrill() {
    setDrillKey(null);
  }
  function handleClose() {
    setDrillKey(null);
    onClose();
  }
  function clearCurrentSection() {
    if (!drillKey) return;
    if (drillKey === 'cena') { onChange({ ...filters, priceRange: [0, maxPrice] }); return; }
    if (drillKey === 'okazje' || drillKey === 'kwiaty' || drillKey === 'kolory' || drillKey === 'kompozycje' || drillKey === 'style') {
      onChange({ ...filters, [drillKey]: [] });
    }
  }

  const drillLabel = ALL_SECTIONS.find(s => s.key === drillKey)?.label ?? '';
  const drillSearchable = drillKey ? isSearchable(optionCount(drillKey)) : false;

  // Portal do document.body — MUSI być poza backdropFilter stacking context.
  // Każda strona kolekcji renderuje CollectionFilters wewnątrz div z
  // backdropFilter:'blur(12px)', co sprawia że position:fixed działa względem
  // tego diva, nie viewportu. createPortal przenosi DOM-nody bezpośrednio
  // pod <body>, całkowicie omijając stacking context rodzica.
  return createPortal(
    <AnimatePresence onExitComplete={releaseScrollLock}>
      {open && (
        <>
          {/* Backdrop — fixed, cały viewport, zIndex 9998 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            onClick={handleClose}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.45)',
              zIndex: 9998,
            }}
          />

          {/* Sheet wrapper — zwykły div (nie motion), więc FM nie dotyka top/bottom.
              top: var(--header-height) kotwi do dołu headera.
              overflow:hidden ukrywa sheet podczas animacji y:100%. */}
          <div style={{
            position: 'fixed',
            top: 'var(--header-height, 56px)',
            left: 0, right: 0, bottom: 0,
            zIndex: 9999,
            overflow: 'hidden',
            pointerEvents: 'none',
          }}>
            {/* motion.div — wyłącznie animacja translateY; height:100% wypełnia wrapper */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 280 }}
              style={{ height: '100%', pointerEvents: 'auto' }}
            >
              {/* Biały kontener — flex column wypełniający cały sheet */}
              <div style={{
                background: '#fff',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                height: '100%',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative',
              }}>
                {/* Handle */}
                <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px', flexShrink: 0 }}>
                  <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(42,31,20,0.15)' }} />
                </div>

                {/* Header */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 20px 14px',
                  borderBottom: `1px solid ${LINE}`,
                  flexShrink: 0,
                }}>
                  <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: DARK }}>
                    Filtry
                  </span>
                  <button
                    onClick={handleClose}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    aria-label="Zamknij filtry"
                  >
                    <X size={18} color={DARK} />
                  </button>
                </div>

                {/* Selected filter chip bars — okazje and kwiaty, above sections list */}
                {(filters.okazje.length > 0 || filters.kwiaty.length > 0) && (
                  <div style={{
                    borderBottom: `1px solid ${LINE}`,
                    paddingTop: 6, paddingBottom: 6,
                    flexShrink: 0,
                  }}>
                    {filters.okazje.length > 0 && (
                      <SelectedFilterBar
                        label="Okazje:"
                        values={filters.okazje}
                        onRemove={v => onChange({ ...filters, okazje: filters.okazje.filter(x => x !== v) })}
                        onClearAll={() => onChange({ ...filters, okazje: [] })}
                      />
                    )}
                    {filters.kwiaty.length > 0 && (
                      <SelectedFilterBar
                        label="Typ kwiatów:"
                        values={filters.kwiaty}
                        onRemove={v => onChange({ ...filters, kwiaty: filters.kwiaty.filter(x => x !== v) })}
                        onClearAll={() => onChange({ ...filters, kwiaty: [] })}
                      />
                    )}
                  </div>
                )}

                {/* Sections menu */}
                <div style={{ overflowY: 'auto', flex: 1, position: 'relative' }}>
                  {SECTIONS.map(s => {
                    const count = sectionCount(s.key);
                    return (
                      <button
                        key={s.key}
                        onClick={() => openDrill(s.key)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          width: '100%', padding: '0 20px',
                          minHeight: 52,
                          background: '#fff',
                          border: 'none', borderBottom: `1px solid ${LINE}`,
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: DARK, fontWeight: count > 0 ? 600 : 400 }}>
                            {s.label}
                          </span>
                          {count > 0 && (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              minWidth: 18, height: 18, borderRadius: '50%', padding: '0 4px',
                              background: GOLD, fontSize: 9.5, color: '#fff', fontWeight: 700,
                              fontFamily: 'DM Sans, sans-serif',
                            }}>{count}</span>
                          )}
                        </div>
                        <ChevronRight size={15} color={MUTED} />
                      </button>
                    );
                  })}
                </div>

                {/* Footer */}
                <div style={{ padding: '14px 20px', borderTop: `1px solid ${LINE}`, display: 'flex', gap: 10, flexShrink: 0 }}>
                  <button
                    onClick={() => { onChange({ ...EMPTY_FILTERS, priceRange: [0, maxPrice] }); setDrillKey(null); }}
                    style={{
                      flex: 1, minHeight: 48, border: `1px solid rgba(42,31,20,0.2)`,
                      background: 'none', cursor: 'pointer',
                      fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em',
                      textTransform: 'uppercase', color: MUTED,
                    }}
                  >
                    Wyczyść
                  </button>
                  <button
                    onClick={handleClose}
                    style={{
                      flex: 2, minHeight: 48, border: 'none',
                      background: DARK, cursor: 'pointer',
                      fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em',
                      textTransform: 'uppercase', color: '#fff',
                    }}
                  >
                    Pokaż wyniki
                  </button>
                </div>

                {/* Drill-down — wjeżdża z prawej, absolute inset:0 relative do białego kontenera */}
                <AnimatePresence>
                  {drillKey && (
                    <motion.div
                      initial={{ x: '100%' }}
                      animate={{ x: 0 }}
                      exit={{ x: '100%' }}
                      transition={{ type: 'spring', damping: 32, stiffness: 320 }}
                      style={{
                        position: 'absolute', inset: 0, zIndex: 5,
                        background: '#fff',
                        display: 'flex', flexDirection: 'column',
                      }}
                    >
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '28px 12px 12px 8px',
                        borderBottom: `1px solid ${LINE}`,
                        flexShrink: 0,
                      }}>
                        <button
                          onClick={closeDrill}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          aria-label="Wstecz"
                        >
                          <ChevronLeft size={18} color={DARK} />
                        </button>
                        <span style={{ flex: 1, fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: DARK }}>
                          {drillLabel}
                        </span>
                        <span style={{ width: 44 }} />
                      </div>

                      {drillSearchable && (
                        <div style={{ padding: '10px 20px 12px', flexShrink: 0 }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            background: CREAM, borderRadius: 999, padding: '9px 14px',
                          }}>
                            <Search size={14} color={MUTED} />
                            <input
                              value={query}
                              onChange={e => setQuery(e.target.value)}
                              placeholder="Szukaj…"
                              style={{
                                border: 'none', background: 'none', outline: 'none', width: '100%',
                                fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: DARK,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      <div style={{ flex: 1, overflowY: 'auto' }}>
                        <DrillContent
                          section={drillKey}
                          query={query}
                          filters={filters}
                          onChange={onChange}
                          maxPrice={maxPrice}
                          kompozycjeOptions={kompozycjeOptions}
                          okazjeOptions={okazjeOptions}
                          kwiatyOptions={kwiatyOptions}
                        />
                      </div>

                      <div style={{ padding: '14px 20px', borderTop: `1px solid ${LINE}`, display: 'flex', gap: 10, flexShrink: 0 }}>
                        <button
                          onClick={clearCurrentSection}
                          style={{
                            flex: 1, minHeight: 48, border: `1px solid rgba(42,31,20,0.2)`,
                            background: 'none', cursor: 'pointer',
                            fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em',
                            textTransform: 'uppercase', color: MUTED,
                          }}
                        >
                          Wyczyść
                        </button>
                        <button
                          onClick={closeDrill}
                          style={{
                            flex: 2, minHeight: 48, border: 'none',
                            background: DARK, cursor: 'pointer',
                            fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em',
                            textTransform: 'uppercase', color: '#fff',
                          }}
                        >
                          Zastosuj
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}

/* ─── Main ────────────────────────────────────────────────── */
interface Props {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  maxPrice: number;
  /** Sort value + change handler injected on mobile so "Sortuj" button lives here */
  sortValue?: string;
  onSortChange?: (v: string) => void;
  sortOptions?: { label: string; value: string; reverse: boolean }[];
  /** Which filter sections to show. Defaults to all. */
  visibleSections?: Section[];
  /** Custom options for the 'kompozycje' panel */
  kompozycjeOptions?: { value: string; label: string; emoji?: string }[];
  /** Custom label for the 'kompozycje' tab */
  kompozycjeLabel?: string;
  /** Subset of OKAZJE to show — computed dynamically from current products */
  okazjeOptions?: { value: string; label: string; emoji: string }[];
  /** Subset of KWIATY to show — computed dynamically from current products */
  kwiatyOptions?: { value: string; label: string; emoji: string }[];
}

export function CollectionFilters({ filters, onChange, maxPrice, sortValue, onSortChange, sortOptions, visibleSections, kompozycjeOptions, kompozycjeLabel, okazjeOptions, kwiatyOptions }: Props) {
  const [open, setOpen] = useState<Section>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check, { passive: true });
    return () => window.removeEventListener('resize', check);
  }, []);

  const toggle = (section: Section) =>
    setOpen(prev => (prev === section ? null : section));

  const show = (s: NonNullable<Section>) => !visibleSections || visibleSections.includes(s);
  const activeCount = [
    show('okazje')     ? filters.okazje.length : 0,
    show('kwiaty')     ? filters.kwiaty.length : 0,
    show('kolory')     ? filters.kolory.length : 0,
    show('kompozycje') ? filters.kompozycje.length : 0,
    show('style')      ? filters.style.length : 0,
    (show('cena') && (filters.priceRange[0] > 0 || filters.priceRange[1] < maxPrice)) ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  /* Active removal chips */
  type Chip = { label: string; remove: () => void };
  const kompOpts = kompozycjeOptions ?? KOMPOZYCJE;
  const activeChips: Chip[] = [
    ...(show('okazje') ? filters.okazje.map(v => ({
      label: (OKAZJE.find(o => o.value === v)?.emoji ?? '') + ' ' + v,
      remove: () => onChange({ ...filters, okazje: filters.okazje.filter(x => x !== v) }),
    })) : []),
    ...(show('kwiaty') ? filters.kwiaty.map(v => ({
      label: (KWIATY.find(k => k.value === v)?.emoji ?? '') + ' ' + v,
      remove: () => onChange({ ...filters, kwiaty: filters.kwiaty.filter(x => x !== v) }),
    })) : []),
    ...(show('kolory') ? filters.kolory.map(v => ({
      label: KOLORY.find(c => c.value === v)?.label ?? v,
      remove: () => onChange({ ...filters, kolory: filters.kolory.filter(x => x !== v) }),
    })) : []),
    ...(show('kompozycje') ? filters.kompozycje.map(v => ({
      label: (kompOpts.find(k => k.value === v)?.emoji ?? '') + ' ' + v,
      remove: () => onChange({ ...filters, kompozycje: filters.kompozycje.filter(x => x !== v) }),
    })) : []),
    ...(show('style') ? filters.style.map(v => ({
      label: (STYLE.find(s => s.value === v)?.emoji ?? '') + ' ' + v,
      remove: () => onChange({ ...filters, style: filters.style.filter(x => x !== v) }),
    })) : []),
    ...(show('cena') && (filters.priceRange[0] > 0 || filters.priceRange[1] < maxPrice)
      ? [{ label: `${filters.priceRange[0]}–${filters.priceRange[1]} zł`, remove: () => onChange({ ...filters, priceRange: [0, maxPrice] }) }]
      : []),
  ];

  const currentSortLabel = sortOptions?.find(o => `${o.value}:${o.reverse}` === sortValue)?.label ?? 'Sortuj';

  /* ── Mobile UI ── */
  if (isMobile) {
    return (
      <>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Filtry button */}
          <button
            onClick={() => setSheetOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', minHeight: 44,
              border: `1.5px solid ${activeCount > 0 ? GOLD : 'rgba(42,31,20,0.18)'}`,
              background: activeCount > 0 ? GOLD : '#fff',
              cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: activeCount > 0 ? '#fff' : DARK, fontWeight: 600,
            }}
          >
            <SlidersHorizontal size={13} color={activeCount > 0 ? '#fff' : GOLD} />
            Filtry
            {activeCount > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 18, height: 18, borderRadius: '50%',
                background: 'rgba(255,255,255,0.3)',
                fontSize: 10, fontWeight: 700, color: '#fff',
              }}>{activeCount}</span>
            )}
          </button>

        </div>

        {/* Active chips row */}
        {activeChips.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {activeChips.map((chip, i) => (
              <span key={i} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px 4px 12px',
                background: GOLD, border: 'none',
                fontFamily: 'DM Sans, sans-serif', fontSize: 10,
                letterSpacing: '0.06em', color: '#fff', fontWeight: 600,
              }}>
                {chip.label}
                <button
                  onClick={chip.remove}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: 4, lineHeight: 0, opacity: 0.75,
                    minWidth: 24, minHeight: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <X size={10} color="#fff" />
                </button>
              </span>
            ))}
          </div>
        )}

        <MobileFilterSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          filters={filters}
          onChange={onChange}
          maxPrice={maxPrice}
          visibleSections={visibleSections}
          kompozycjeOptions={kompozycjeOptions}
          kompozycjeLabel={kompozycjeLabel}
          okazjeOptions={okazjeOptions}
          kwiatyOptions={kwiatyOptions}
        />
      </>
    );
  }

  /* ── Desktop UI (unchanged) ── */
  return (
    <div>
      {/* ── Tab row ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {show('kompozycje') && <Tab label={kompozycjeLabel ?? 'Kompozycja'} active={filters.kompozycje.length > 0} open={open === 'kompozycje'} onClick={() => toggle('kompozycje')} />}
        {show('style')      && <Tab label="Styl bukietu"   active={filters.style.length > 0}      open={open === 'style'}      onClick={() => toggle('style')}      />}
        {show('okazje')     && <Tab label="Okazja"         active={filters.okazje.length > 0}   open={open === 'okazje'}   onClick={() => toggle('okazje')}   />}
        {show('kwiaty')     && <Tab label="Rodzaj kwiatów" active={filters.kwiaty.length > 0}   open={open === 'kwiaty'}   onClick={() => toggle('kwiaty')}   />}
        {show('kolory')     && <Tab label="Kolor"          active={filters.kolory.length > 0}   open={open === 'kolory'}   onClick={() => toggle('kolory')}   />}
        {show('cena')       && <Tab label="Cena"
          active={filters.priceRange[0] > 0 || filters.priceRange[1] < maxPrice}
          open={open === 'cena'} onClick={() => toggle('cena')}
        />}

        {activeChips.length > 0 && (
          <button
            onClick={() => { onChange({ ...EMPTY_FILTERS, priceRange: [0, maxPrice] }); setOpen(null); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '8px 10px', minHeight: 44, border: 'none', background: 'none',
              fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: MUTED,
              cursor: 'pointer', textDecoration: 'underline',
            }}
          >
            <X size={11} /> Wyczyść
          </button>
        )}
      </div>

      {/* ── Expanded panel ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            key={open}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{
              paddingTop: 20, paddingBottom: 6,
              borderTop: `1px solid rgba(210,60,120,0.25)`,
              marginTop: 14,
            }}>
              <PanelContent
                section={open}
                filters={filters}
                onChange={onChange}
                maxPrice={maxPrice}
                kompozycjeOptions={kompozycjeOptions}
                okazjeOptions={okazjeOptions}
                kwiatyOptions={kwiatyOptions}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Active chips ── */}
      {activeChips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14 }}>
          {activeChips.map((chip, i) => (
            <span key={i} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 10px 4px 12px',
              background: GOLD, border: 'none',
              fontFamily: 'DM Sans, sans-serif', fontSize: 10,
              letterSpacing: '0.06em', color: '#fff', fontWeight: 600,
            }}>
              {chip.label}
              <button onClick={chip.remove} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 0, opacity: 0.75 }}>
                <X size={10} color="#fff" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
